import type { SkillEnum } from '@/apiTypes/SkillsTypes';
import type { BuildType } from '@/services/ApiService.ts';
import type { JourneyDetail, JourneyListItem } from '@/pages/Journeys/mockData/types';

export interface CreateUserJourneyResponse {
    id: string;
    name: string;
    occupationId: string | null;
    journeyTypeId: string;
    status: number;
    createdTaskCount: number;
    createdSkillCount: number;
}

export const userJourneyApiEndpoints = (build: BuildType) => ({
    listUserJourneys: build.query<JourneyListItem[], void>({
        query: () => 'user-journeys',
        providesTags: ['UserJourneys'],
    }),
    getUserJourney: build.query<JourneyDetail, string>({
        query: id => `user-journeys/${id}`,
        providesTags: (_result, _error, id) => [{ type: 'UserJourneys', id }],
    }),
    createUserJourney: build.mutation<CreateUserJourneyResponse, { occupationId: string }>({
        query: body => ({
            url: 'user-journeys',
            method: 'POST',
            body,
        }),
        invalidatesTags: ['UserJourneys'],
    }),
    updateUserJourneyName: build.mutation<JourneyDetail, { id: string; name: string }>({
        query: ({ id, name }) => ({
            url: `user-journeys/${id}`,
            method: 'PATCH',
            body: { name },
        }),
        invalidatesTags: (_result, _error, { id }) => ['UserJourneys', { type: 'UserJourneys', id }],
    }),
    completeUserJourney: build.mutation<JourneyDetail, string>({
        query: journeyId => ({
            url: `user-journeys/${journeyId}/complete`,
            method: 'POST',
        }),
        invalidatesTags: (_result, _error, journeyId) => ['UserJourneys', { type: 'UserJourneys', id: journeyId }],
    }),
    deleteUserJourney: build.mutation<void, string>({
        query: id => ({
            url: `user-journeys/${id}`,
            method: 'DELETE',
        }),
        invalidatesTags: ['UserJourneys'],
    }),
    createUserJourneyTask: build.mutation<
        JourneyDetail,
        { journeyId: string; journeyMilestoneId: string; title: string; description?: string }
    >({
        query: ({ journeyId, ...body }) => ({
            url: `user-journeys/${journeyId}/tasks`,
            method: 'POST',
            body,
        }),
        invalidatesTags: (_result, _error, { journeyId }) => ['UserJourneys', { type: 'UserJourneys', id: journeyId }],
    }),
    getJourneyMilestoneRecommendedTasks: build.query<
        Array<{ id: string; title: string }>,
        { journeyId: string; milestoneId: string }
    >({
        query: ({ journeyId, milestoneId }) => `user-journeys/${journeyId}/milestones/${milestoneId}/recommended-tasks`,
        providesTags: ['UserJourneys'],
    }),
    getJourneyRecommendedSkills: build.query<Array<{ id: string; title: string; skillType: SkillEnum }>, string>({
        query: journeyId => `user-journeys/${journeyId}/recommended-skills`,
        providesTags: (_result, _error, journeyId) => [{ type: 'UserJourneys', id: journeyId }],
    }),
    patchUserJourneyTask: build.mutation<JourneyDetail, { taskId: string; completed: boolean }>({
        query: ({ taskId, completed }) => ({
            url: `user-journey-tasks/${taskId}`,
            method: 'PATCH',
            body: { completed },
        }),
        invalidatesTags: ['UserJourneys'],
    }),
    deleteUserJourneyTask: build.mutation<JourneyDetail, string>({
        query: taskId => ({
            url: `user-journey-tasks/${taskId}`,
            method: 'DELETE',
        }),
        invalidatesTags: ['UserJourneys'],
    }),
    deleteUserJourneySkillLink: build.mutation<JourneyDetail, string>({
        query: journeySkillId => ({
            url: `user-journey-skills/${journeySkillId}`,
            method: 'DELETE',
        }),
        invalidatesTags: ['UserJourneys'],
    }),
    addUserJourneySkillLinks: build.mutation<
        JourneyDetail,
        { journeyId: string; skillIds: string[]; userCustomSkillIds?: string[] }
    >({
        query: ({ journeyId, skillIds = [], userCustomSkillIds = [] }) => ({
            url: `user-journeys/${journeyId}/skill-links`,
            method: 'POST',
            body: { skillIds, userCustomSkillIds },
        }),
        invalidatesTags: (_result, _error, { journeyId }) => ['UserJourneys', { type: 'UserJourneys', id: journeyId }],
    }),
});
